import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { setCredentials, setAuthReady } from "./redux/slices/auth";
import { login as authLogin } from "./services/authService";
import { Capacitor } from "@capacitor/core";
import { addLog, logDeviceInfo, logNetworkInfo } from "./logger";
import { initFCM } from "./fcm";

// Get credentials from environment variables
// Fallback to defaults for development (should be set via .env in production)
const username = import.meta.env.VITE_API_USERNAME || "super_admin";
const password = import.meta.env.VITE_API_PASSWORD || "user0123";
const scope = "farm_manager";

const AuthLoader = () => {
  const dispatch = useDispatch();
  const [loginAttempts, setLoginAttempts] = useState(0);

  useEffect(() => {
    addLog("======= APP STARTUP =======");
    addLog(
      `Platform: ${Capacitor.isNativePlatform() ? "Mobile (Native)" : "Web"}`
    );
    logDeviceInfo();
    logNetworkInfo();

    const autoLogin = async () => {
      try {
        addLog(`Attempting login (attempt #${loginAttempts + 1})...`);
        const startTime = Date.now();

        const result = await authLogin({ username, password, scope });
        const duration = Date.now() - startTime;

        addLog(`Login successful in ${duration}ms: Token received`);

        const accessToken = result.token || result.access_token;
        const newRefreshToken = result.refreshToken || result.refresh_token;

        if (!accessToken) {
          addLog("ERROR: Empty token received from login response");
          addLog("Response data: " + JSON.stringify(result).substring(0, 200));
          dispatch(setAuthReady(true));
          return;
        }

        const cleanToken = (accessToken as string).trim();

        addLog(
          `Token length: ${cleanToken.length}, first 10 chars: ${cleanToken.substring(0, 10)}...`
        );

        dispatch(
          setCredentials({
            token: cleanToken,
            refreshToken: (newRefreshToken as string) || "",
          })
        );

        localStorage.setItem("token", cleanToken);
        if (newRefreshToken) {
          localStorage.setItem("refreshToken", newRefreshToken as string);
        }

        addLog("Setting auth state to READY");
        dispatch(setAuthReady(true));

        initFCM();
      } catch (err) {
        addLog(
          `Login failed: ${err instanceof Error ? err.message : JSON.stringify(err)}`
        );

        if (loginAttempts < 3) {
          const retryDelay = Math.pow(2, loginAttempts) * 1000;
          addLog(
            `Retrying login in ${retryDelay / 1000} seconds... (Attempt ${loginAttempts + 1})`
          );
          setTimeout(() => {
            setLoginAttempts((prev) => prev + 1);
            logNetworkInfo();
            autoLogin();
          }, retryDelay);
        } else {
          addLog("Maximum login attempts reached, setting auth ready regardless");
          dispatch(setAuthReady(true));
        }
      }
    };

    addLog("Starting authentication process...");
    autoLogin();
  }, [dispatch, loginAttempts]);

  return null;
};

export default AuthLoader;
